import { useContext, useState } from 'react';
import axios from 'axios';
import { ToastMessageContext } from 'common/components/ToastMessage';
import { IAnyType } from 'common/types/common';
import { ENV_VARIABLES } from 'core/config';
import { useFormik } from 'formik';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import * as Yup from 'yup';

export const useHook = ({ isProject }: { isProject: boolean }) => {
  const router = useRouter();
  const { i18n, t } = useTranslation('pageContact');

  const { showMessage } = useContext(ToastMessageContext);

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const getInitialValues = () => {
    const commonValues = {
      about: '',
      biggestOperationalBottleneck: '',
      budget: null,
      companyName: '',
      currentToolsOrSystems: '',
      email: '',
      name: '',
      phoneNumber: '',
      howDidYouHearAboutUs: '',
      message: '',
      timeline: null,
    };
    if (isProject) {
      return { ...commonValues, services: [], typeOfProject: null };
    }
    return commonValues;
  };

  const getSchemaObject = (): IAnyType => {
    if (isProject) {
      return {
        about: Yup.string(),
        biggestOperationalBottleneck: Yup.string(),
        budget: Yup.object().required(t('form.budget.required')),
        name: Yup.string().required(t('form.name.required')),
        email: Yup.string().email(t('form.email.invalid')).required(t('form.email.required')),
        phoneNumber: Yup.string(),
        companyName: Yup.string(),
        currentToolsOrSystems: Yup.string(),
        howDidYouHearAboutUs: Yup.string(),
        message: Yup.string().required(t('form.message.required')),
        timeline: Yup.object().required(t('form.timeline.required')),
        typeOfProject: Yup.object().required(t('form.typeOfProject.required')),
        services: Yup.array()
          .min(1, t('form.service.required'))
          .required(t('form.service.required')),
      };
    }
    return {
      about: Yup.string(),
      companyName: Yup.string(),
      email: Yup.string().email(t('form.email.invalid')).required(t('form.email.required')),
      howDidYouHearAboutUs: Yup.string(),
      name: Yup.string().required(t('form.name.required')),
      phoneNumber: Yup.string(),
      message: Yup.string().required(t('form.message.required')),
    };
  };

  const schema: IAnyType = Yup.object().shape(getSchemaObject());

  const handleSave = async (values: IAnyType, form: IAnyType) => {
    if (isSubmitting) {
      return;
    }
    const {
      about,
      biggestOperationalBottleneck,
      budget,
      companyName,
      currentToolsOrSystems,
      email,
      howDidYouHearAboutUs,
      name,
      phoneNumber,
      message,
      timeline,
      typeOfProject,
    } = values;
    if (about.length) {
    } else {
      const data = {
        about,
        biggestOperationalBottleneck,
        budget: budget?.label || '',
        companyName,
        currentToolsOrSystems,
        email,
        howDidYouHearAboutUs,
        name,
        phoneNumber,
        message,
        timeline: timeline?.label || '',
        typeOfProject: typeOfProject?.label || '',
        services: values.services?.length
          ? values.services.map((x: IAnyType) => x.label).join(', ')
          : '',
      };

      setIsSubmitting(true);
      try {
        const res = await axios.post(`${ENV_VARIABLES.EMAIL_URL}/mail/nordit-contact-form`, data);
        setIsSubmitting(false);
        if (res.data.success) {
          form.setValues(getInitialValues());
          // GA4 event
          if (typeof window !== 'undefined' && window) {
            const win: IAnyType = window;
            win.dataLayer = win.dataLayer || [];
            win.dataLayer.push({
              event: isProject ? 'contact-project-form-submit' : 'contact-form-submit',
              email: data.email,
            });
          }
          if (i18n.language === 'en') {
            router.push({ pathname: `/thank-you${isProject ? '-project' : ''}` }, undefined, {
              shallow: true,
            });
          } else {
            router.push(
              { pathname: `/${i18n.language}/thank-you${isProject ? '-projekt' : ''}` },
              undefined,
              { shallow: true },
            );
          }
        } else if (res.data.error) {
          showMessage({
            duration: 5000,
            message: `${t('errors.thereAreSomeErrorsInYourForm')} ${t(
              'errors.pleaseCheckTheFormAndTryAgain',
            )}`,
            type: 'error',
          });
        }
      } catch (error: IAnyType) {
        setIsSubmitting(false);
        const messages: string[] = [];
        const { message: messageError } = error?.response?.data || ({} as IAnyType);
        if (Array.isArray(messageError)) {
          messageError.forEach((item: string) => {
            messages.push(t(`errors.${item}`));
          });
        }

        if (messages.length) {
          showMessage({ duration: 5000, message: messages.join('<br />'), type: 'error' });
        } else {
          showMessage({
            duration: 5000,
            message: `${t('errors.thereAreSomeErrorsInYourForm')} ${t(
              'errors.pleaseCheckTheFormAndTryAgain',
            )}`,
            type: 'error',
          });
        }
      }
    }
  };

  const formik = useFormik({
    initialValues: getInitialValues(),
    validationSchema: schema,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: handleSave,
  });

  const fieldProps = (field: string) => {
    const data: IAnyType = formik.getFieldProps(field);
    const { error: fieldError, touched } = formik.getFieldMeta(field);

    data.onChangeText = (v: IAnyType): void => {
      formik.handleChange(field)({ target: { value: v } } as IAnyType);
      if (fieldError) {
        formik.validateField(field);
      }
    };
    if (field === 'typeOfProject' || field === 'budget' || field === 'timeline') {
      data.onChange = (v: IAnyType): void => {
        formik.setFieldValue(field, v);
        if (fieldError) {
          formik.validateField(field);
        }
      };
    }
    return { ...data, error: fieldError, touched, disabled: isSubmitting };
  };

  const handleServiceClick = (item: { label: string; value: string }) => () => {
    const values = formik.values as IAnyType;
    let value: { label: string; value: string }[] = [...values.services];
    if (value.some(x => x.value === item.value)) {
      value = value.filter(x => x.value !== item.value);
    } else {
      value = [...value, item];
    }
    formik.setFieldValue('services', value);
    formik.validateField('services');
  };

  const validateStep = async (fields: string[]) => {
    const touchedFields = fields.reduce(
      (acc, field) => ({ ...acc, [field]: true }),
      {} as Record<string, boolean>,
    );

    formik.setTouched({ ...(formik.touched as IAnyType), ...touchedFields });

    const errors = (await formik.validateForm()) as IAnyType;

    return !fields.some(field => !!errors[field]);
  };

  return {
    handleServiceClick,
    isSubmitting,
    fieldProps,
    formik,
    schema,
    validateStep,
  };
};
